/**
 * control-api is a sibling host of the Mesh UI under the same registrable
 * domain: `dev1-core.hydden.ai` is paired with `dev1-control.hydden.ai`, and in
 * prod both sides drop the environment prefix (`core.hydden.ai` pairs with
 * `control.hydden.ai`). Being same-site is what lets the browser attach
 * Control's session cookie, so the pairing is load-bearing, not cosmetic.
 *
 * The origin must be derived at runtime rather than baked in: one portald image
 * serves every environment, so an origin chosen at build time would be correct
 * in exactly one of them.
 */

const MESH_UI_LABEL = 'core';
const CONTROL_LABEL = 'control';

/**
 * Dev-only escape hatch, for local development where there is no same-site pair
 * to derive from (see the endpoint contract, "Local dev does not work yet").
 *
 * Gated on `import.meta.env.DEV` rather than on a convention that it be left
 * unset. `import.meta.env` is substituted at build time, so "must stay unset in
 * deployed images" would be a pipeline invariant with nothing enforcing it — and
 * one release build carrying a value would pin every environment to that single
 * origin, which is the exact failure the runtime derivation exists to prevent.
 * A production bundle cannot reach this branch at all.
 */
function configuredOrigin(): string | null {
  if (!import.meta.env.DEV) {
    return null;
  }

  const raw = import.meta.env.VITE_CONTROL_API_ORIGIN;
  if (typeof raw !== 'string' || raw.trim() === '') {
    return null;
  }
  return raw.trim().replace(/\/+$/, '');
}

/**
 * Map the Mesh UI's own hostname onto control-api's. Returns `null` when the
 * hostname is not a recognised Mesh UI host, which is the case for `localhost`
 * and for any preview host that does not follow the `*-core` convention.
 */
export function controlHostFromMeshUiHost(hostname: string): string | null {
  const labels = hostname.split('.');
  const first = labels[0];

  if (first === MESH_UI_LABEL) {
    labels[0] = CONTROL_LABEL;
    return labels.join('.');
  }

  if (first?.endsWith(`-${MESH_UI_LABEL}`)) {
    const envPrefix = first.slice(0, -MESH_UI_LABEL.length);
    labels[0] = `${envPrefix}${CONTROL_LABEL}`;
    return labels.join('.');
  }

  return null;
}

/**
 * Absolute origin for control-api, or `null` when it cannot be determined.
 *
 * Callers must use the absolute origin for every control-api request: the Mesh
 * UI's Vite proxy routes `/api/v1` to mesh-core, and mesh serves its own
 * `/api/v1/me`, so a relative path silently reaches the wrong backend.
 */
export function controlApiOrigin(
  location: Pick<Location, 'hostname' | 'protocol'> = window.location
): string | null {
  const override = configuredOrigin();
  if (override) {
    return override;
  }

  const host = controlHostFromMeshUiHost(location.hostname);
  if (!host) {
    return null;
  }

  return `${location.protocol}//${host}`;
}

/**
 * Where to send a user who has no Control session. Control owns the login form;
 * `redirect` brings them back to the page they were on. T02 (HYD-1363) is what
 * makes this parameter work on the normal login path and validates it.
 */
export function controlLoginUrl(
  location: Pick<Location, 'hostname' | 'protocol' | 'href'> = window.location
): string | null {
  const origin = controlApiOrigin(location);
  if (!origin) {
    return null;
  }

  return `${origin}/auth?redirect=${encodeURIComponent(location.href)}`;
}
